<template>
	<div v-if="selectedStudy && currentTab" class="NutritionReports">
		<h2 class="left-align">View Respondent Nutrition Reports</h2>
		<p class="p1 left-align">
			The table below provides some information about each respondent’s intake
			and allows you to view their Respondent Nutrition Report (RNR).  The RNR
			provides information to respondents regarding how their intake on the
			reporting day(s) compares to country-specific dietary guidance and
			nutrient requirements. See more information
			<a href="https://epi.grants.cancer.gov/asa24/respondent/nutrition-report.html#:~:text=How%20are%20ASA24%20Respondent%20Nutrition,they%20are%20pregnant%20or%20lactating" target="_blank" aria-label="ASA24 Respondent Nutrition Report info - New Window">here</a>.
		</p>
		<br>
		<div class="nutrition-reports-filter-container">
			<v-row>
				<v-col>
					<v-row no-gutters>
						<v-col>
							<p class="p3 left-align">Filter by username</p>
						</v-col>
					</v-row>
					<v-row no-gutters>
						<v-col>
							<v-text-field
								ref="firstFocusable"
								outlined
								placeholder="Username"
                label="Username"
                single-line
                hide-details
                v-model="filterUserNameValue"
								id="filterByUsernameViewNutrition"
								:aria-label="'Filter by username: ' + filterUserNameValue"
            ></v-text-field>
						</v-col>
					</v-row>
				</v-col>
				<v-divider vertical></v-divider>
				<v-col>
					<v-row no-gutters>
						<v-col md="6">
							<p class="p3 left-align">Start Date:</p>
							<v-menu  v-model="startDateMenu" :close-on-content-click="false" offset-y tile>
  							<template v-slot:activator="{ on, attrs }">
								<v-text-field
								hide-details="auto"
								outlined
								type="date"
								:rules="startDateRules"
								v-model="filterStartDateValue"
								id="form-start-date"
								v-bind="attrs"
								v-on="on"
								@keydown.tab='startDateMenu = false'
								:aria-label="'Start Date: ' + filterStartDateValue"
							>
							</v-text-field>
								</template>
								<v-date-picker v-model="filterStartDateValue" color="#42729B" style="margin: 0px; float: left" @input="startDateMenu = false"></v-date-picker>
							</v-menu>
						</v-col>
						<v-col md="6">
							<p class="p3 left-align">End Date:</p>
							<v-menu  v-model="endDateMenu" :close-on-content-click="false" offset-y tile>
  							<template v-slot:activator="{ on, attrs }">
								<v-text-field
								hide-details="auto"
								outlined
								type="date"
								:rules="endDateRules"
								v-model="filterEndDateValue"
								id="form-end-date"
								v-bind="attrs"
      					v-on="on"
								@keydown.tab='endDateMenu = false'
								:aria-label="'End Date: ' + filterEndDateValue"
							>
							</v-text-field>
								</template>
								<v-date-picker v-model="filterEndDateValue" color="#42729B" style="margin: 0px; float: left" @input="endDateMenu = false"></v-date-picker>
							</v-menu>
						</v-col>
					</v-row>
				</v-col>
				<v-divider vertical></v-divider>
				<v-col>
					<v-row no-gutters>
						<v-col>
							<p class="p3 left-align">Respondent Request:</p>
						</v-col>
					</v-row>

					<fieldset class="border-0">
						<legend class="d-sr-only">
							Respondent Request:
						</legend>

						<v-row no-gutters>
							<v-col>
								<v-checkbox v-model="yesFilter"
														:label="'Yes'"></v-checkbox>
							</v-col>
							<v-col>
								<v-checkbox v-model="noFilter"
														:label="'No'"></v-checkbox>
							</v-col>
							<v-col>
								<v-checkbox v-model="naFilter"
														:label="'n/a'"></v-checkbox>
							</v-col>
							<v-col>
								<v-btn @click="clearFilters()" class="btn-default" text>Clear</v-btn>
							</v-col>
						</v-row>

					</fieldset>

				</v-col>
			</v-row>
		</div>
		<br>
		<div v-show="headers.length !== 0" class="nutrition-reports-table-container">
			<v-data-table
				:headers="headers"
				:items="recalls"
				item-key="recallId"
				class="elevation-1"
				:page.sync="page"
				:items-per-page="itemsPerPage"
				hide-default-footer
				@page-count="pageCount = $event"
				:loading="isLoading"
			>
				<template v-for="h in headers" v-slot:[`header.${h.value}`]="{ header }">
					<v-tooltip  :disabled="h.hover === ''" :key="h.value" bottom color="#333">
						<template v-slot:activator="{ on }">
							<span v-on="on" class="columnTitleText" tabIndex="0" v-on:keyup.enter="(e) => e.target.click()">{{h.text}}</span>
						</template>
						<span>{{h.hover}}</span>
					</v-tooltip>
				</template>
				<template v-slot:item.source="{ item }">
					{{ getFormattedRespondentRequest(item.source, item.refused) }}
				</template>
				<template v-slot:item.dateReportedLocal="{ item }">
					{{ getFormattedDate(item.dateReportedLocal) }}
				</template>
				<template v-slot:item.kCal="{ item }">
					{{ (item.kCal).toFixed(3) }}
				</template>
				<template v-slot:item.reportBtn="{ item }">
					<v-btn v-if="!item.age" text class="btn-link-left text-none" @click="openCreateReportDialog(item)">Create</v-btn>
					<v-btn v-else text class="btn-link-left text-none" @click="openViewReportDialog(item)">View</v-btn>
				</template>
			</v-data-table>
			<div class="pagination-container">
				<v-row>
					<v-col :cols="2">
						<v-text-field
							:value="itemsPerPage"
							label="Items per page"
							type="number"
							min="1"
							max="15"
							@input="itemsPerPage = parseInt($event, 10)"
						></v-text-field>
					</v-col>
					<v-col :cols="10">
						<v-pagination
							v-model="page"
							:length="pageCount"
						></v-pagination>
					</v-col>
				</v-row>
			</div>
		</div>
		<foe-dialog
      v-model="viewReportDialog"
			:dialogW="viewReportDialog"
      persistent
      max-width="600"
			class="view-report-dialog"
    >
      <v-card v-if="rowReportInfo" class="view-report-card">
        <v-card-title class="headline">
          View Nutrition Report
        </v-card-title>
        <v-card-text>
					<p class="p2 left-align">
						Information about respondent <strong>{{rowReportInfo.userName}}</strong> for reporting date <strong>{{getFormattedDate(rowReportInfo.dateReportedLocal)}}</strong>
					</p>
					<v-autocomplete
						ref="autocomplete2"
						v-model="rowReportInfo.language"
						:items="getVersionLanguages"
						item-value="languageValue"
						item-text="languageName"
						outlined
						label="Report Language"
						:aria-expanded=isExpanded2
						@focus=checkExpandedTwo
						@click=checkExpandedTwo
						@blur=checkExpandedTwo
						@update:list-index=checkExpandedTwo
						@update:search-input=checkExpandedTwo
					>
						<template v-slot:no-data><div></div></template>
					</v-autocomplete>
				</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
						class="btn-default"
            @click="viewReportDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            text
						class="btn-default"
            @click="viewReport()"
          >
            View Report
          </v-btn>
        </v-card-actions>
      </v-card>
    </foe-dialog>
		<foe-dialog
      v-model="createReportDialog"
			:dialogW="createReportDialog"
      persistent
      max-width="600"
			class="create-report-dialog"
    >
      <v-card v-if="rowReportInfo" class="create-report-card">
        <v-card-title class="headline">
          View Nutrition Report
        </v-card-title>
        <v-card-text>
					<p class="p2 left-align">
						Specify information about respondent <strong>{{rowReportInfo.userName}}</strong> for reporting date <strong>{{rowReportInfo.dateReported}}</strong>
					</p>
					<v-autocomplete
						ref="autocomplete3"
						v-model="rowReportInfo.language"
						:items="getVersionLanguages"
						item-value="languageValue"
						item-text="languageName"
						outlined
						label="Report Language"
						:aria-expanded=isExpanded3
						@focus=checkExpandedThree
						@click=checkExpandedThree
						@blur=checkExpandedThree
						@update:list-index=checkExpandedThree
						@update:search-input=checkExpandedThree
					>
					<template v-slot:no-data><div></div></template>
					</v-autocomplete>
					<v-text-field
						outlined
						label="Age"
						single-line
						hide-details
						v-model="rowReportInfo.age"
					></v-text-field>
					<v-radio-group
						v-model="rowReportInfo.gender"
						label="Sex:"
						row
					>
						<v-radio
							value="F"
							label="Female"
						>
						</v-radio>
						<v-radio
							value="M"
							label="Male"
						>
						</v-radio>
					</v-radio-group>
					<div v-if="rowReportInfo.gender === 'F'">
						<v-radio-group
							v-model="rowReportInfo.pregnant"
							label="Pregnant?"
							row
						>
							<v-radio
								:value="true"
								label="Yes"
							>
							</v-radio>
							<v-radio
								:value="false"
								label="No"
							>
							</v-radio>
						</v-radio-group>
						<v-radio-group
							v-model="rowReportInfo.breastFeed"
							label="Breastfeeding?"
							row
						>
							<v-radio
								:value="true"
								label="Yes"
							>
							</v-radio>
							<v-radio
								:value="false"
								label="No"
							>
							</v-radio>
						</v-radio-group>
					</div>
				</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
						class="btn-default"
            @click="cancelCreateReport()"
          >
            Cancel
          </v-btn>
          <v-btn
            text
						class="btn-default"
            @click="viewReport()"
          >
            View Report
          </v-btn>
        </v-card-actions>
      </v-card>
    </foe-dialog>
	</div>
</template>

<script>
import { analysisApiService } from '@/services/analysisApiService.js';
import { authTokenService } from '@/services/authTokenService.js';
import { utilityService } from '@/services/utilityService.js';
import { moduleService } from '@/services/moduleService.js';

export default {
	name: 'NutritionReports',
	props: {
	},
	data: () => ({
		studyUuid: '',
		recalls: [],
		page: 1,
		pageCount: 0,
		itemsPerPage: 10,
		filterStartDateValue: null,
		filterEndDateValue: null,
		filterUserNameValue: '',
		yesFilter: false,
		noFilter: false,
		naFilter: false,
		rowReportInfo: null,
		viewReportDialog: false,
		createReportDialog: false,
		isLoading: false,
		isProfileDataCreated: false,
		startDateRules: [(v) => (v && v !== '') || 'Improper date or missing value(s)'],
		endDateRules: [(v) => (v && v !== '') || 'Improper date or missing value(s)'],
		startDateMenu: false,
		endDateMenu: false,
		isExpaned2: 'false',
		isExpaned3: 'false'
	}),
	mounted () {
		this.endDateRules.push(v => v >= this.filterStartDateValue || 'Date must be greater than or equal to the start date');
		this.isExpanded2 = this.$refs.autocomplete2.isMenuActive.toString()
		this.isExpanded3 = this.$refs.autocomplete3.isMenuActive.toString()
	},
	watch: {
		isProfileDataCreated: function (newValue, oldValue) {
			if (newValue !== oldValue && newValue === true) {
				new Promise(resolve => setTimeout(resolve, 2000)).then(() => {
					this.loadData();
				});
			}
			this.isProfileDataCreated = false;
		},
		rowReportInfo: function (newVal, oldVal) {
			this.$nextTick(function () {
			// 2021.10.20 - Sebastian Calle assisted with the following idea/solution
			// Code that will run only after the entire view has been rendered
			// var b = document.getElementById('form-study-purpose');
			// b.setAttribute('aria-labelledby', 'form-study-purpose-label')
			// 2021.10.25 - Yevgeniy editted above to target class name and remove aria-labelledby attribute for all elements
			// 2022.06.29 - CHT adding to nutrition reports
				var vRadioGroup = document.getElementsByClassName('v-input--radio-group__input');
				for (let i = 0; i < vRadioGroup.length; i++) {
					vRadioGroup[i].removeAttribute('aria-labelledby');
				}
			});
		}
	},
	computed: {
		headers: function () {
			const headerArr = [
				{ text: 'Username', value: 'userName', hover: '', class: 'data-table-header', filter: this.filterUsername, align: 'center' },
				{ text: 'Reporting Date', value: 'dateReportedLocal', hover: '', class: 'data-table-header', filter: this.filterDate, align: 'center' },
				{ text: 'Status', value: 'recallStatus', hover: '', class: 'data-table-header', align: 'center' },
				{ text: 'Respondent Request?', value: 'source', hover: '', class: 'data-table-header', filter: this.filterStatus, align: 'center' },
				{ text: 'Calories (kCal.)', value: 'kCal', hover: '', class: 'data-table-header', align: 'center' },
				{ text: 'Number of eating occasions', value: 'numMeals', hover: '', class: 'data-table-header', align: 'center' },
				{ text: 'Number of food codes', value: 'numFoodCodes', hover: '', class: 'data-table-header', align: 'center' }
			];

			// Only add the RNR column if the study version supports RNR module (No Australia studies)
			if (moduleService.isModuleRNRAllowed(this.selectedStudy.version, '').isModuleAllowed) {
				headerArr.push({ text: 'Nutrition Report', value: 'reportBtn', hover: '', class: 'data-table-header', sortable: false, align: 'center' });
			}

			return headerArr;
		},
		selectedStudy: function () {
			// If the selectedStudy changes, the radio buttons and checkbox properties need to be updated.
			if (this.studyUuid !== this.$store.getters.getSelectedStudy.studyUuid) {
				this.updateSelected();
			}
			return this.$store.getters.getSelectedStudy;
		},
		currentTab: function () {
			const tabPosition = {
				tabIndex: parseInt(this.$store.getters.getCurrentStudyTabIndex),
				subTabIndex: parseInt(this.$store.getters.getCurrentStudySubTabIndex)
			};

			this.checkTabMatch(tabPosition);

			return tabPosition;
		},
		getVersionLanguages: function () {
			const versionId = this.selectedStudy.version;
			return this.$store.getters.getStudyVersion(versionId).languages;
		}
	},
	methods: {
		updateSelected () {
			this.studyUuid = this.$store.getters.getSelectedStudy.studyUuid;
			this.loadData();
		},
		async loadData () {
			this.isLoading = true;
			console.log('loadData');
			var rawRecalls;
			// Hard code the parameters so we still do the paging, sorting, filtering client side
			await analysisApiService.loadRecalls(this.selectedStudy.studyId, 1, 10000, 'userName', 'ASC', null, null, null, null)
				.then(async function (response) {
					if (response && response.data) {
						rawRecalls = response.data.trackDataList;
					}
				});
			this.recalls = this.translateEnums(rawRecalls);
			this.isLoading = false;
		},
		async checkTabMatch ({ tabIndex, subTabIndex } = {}) {
			var tabsPos = await utilityService.getTabsPositionByName('View Respondent Nutrition Reports', this.$studyTabConfig);
			if (tabsPos.tabPos === tabIndex && tabsPos.subtabPos === subTabIndex) {
				this.$refs.firstFocusable.focus();
				this.loadData();
			}
		},
		filterUsername (val) {
			if (!this.filterUserNameValue) {
				return true;
			}

			return val.toLowerCase().includes(this.filterUserNameValue.toLowerCase());
		},
		filterStatus (val) {
			if (!this.yesFilter && !this.noFilter && !this.naFilter) {
				return true;
			}

			if (this.yesFilter && this.noFilter && this.naFilter) {
				return true;
			}

			if (this.yesFilter && this.noFilter && !this.naFilter) {
				if (val.toLowerCase().includes('yes') || val.toLowerCase().includes('no')) {
					return true
				}
				return false;
			}

			if (this.yesFilter && !this.noFilter && this.naFilter) {
				if (val.toLowerCase().includes('yes') || val.toLowerCase().includes('n/a')) {
					return true
				}
				return false;
			}

			if (!this.yesFilter && this.noFilter && this.naFilter) {
				if (val.toLowerCase().includes('n/a') || val.toLowerCase().includes('no')) {
					return true
				}
				return false;
			}

			if (this.yesFilter) {
				return val.toLowerCase().includes('yes');
			}

			if (this.noFilter) {
				return val.toLowerCase().includes('no');
			}

			if (this.naFilter) {
				return val.toLowerCase().includes('n/a');
			}
		},
		filterDate (val) {
			if (!this.filterStartDateValue && !this.filterEndDateValue) {
				return true;
			}

			const selectedStartDate = new Date(this.filterStartDateValue + 'T00:00:00Z').getTime();
			const selectedEndDate = new Date(this.filterEndDateValue + 'T00:00:00Z').getTime() + 86399999; // adding 1 less millisecond of 24 hours of milliseconds (86400000) is essentially the 'end' timestamp for that date's range of miliseconds
			const recallDate = new Date(val).getTime();

			if (this.filterStartDateValue && this.filterEndDateValue) {
				return (recallDate <= selectedEndDate ? (recallDate >= selectedStartDate) : false);
			}

			if (this.filterStartDateValue) {
				return recallDate >= selectedStartDate;
			}

			if (this.filterEndDateValue) {
				return recallDate <= selectedEndDate;
			}
		},
		clearFilters () {
			this.filterUserNameValue = '';
			this.yesFilter = false;
			this.noFilter = false;
			this.naFilter = false;
			this.filterStartDateValue = null;
			this.filterEndDateValue = null;
		},
		translateEnums (rawRecalls) {
			for (var i = 0; i < rawRecalls.length; i++) {
				switch (parseInt(rawRecalls[i].language)) {
				case 0:
					rawRecalls[i].language = 'Unknown';
					break;
				case 1:
					rawRecalls[i].language = 'English';
					break;
				case 2:
					rawRecalls[i].language = 'Spanish';
					break;
				case 3:
					rawRecalls[i].language = 'French';
					break;
				case 4:
					rawRecalls[i].language = 'Polish';
					break;
				}

				switch (rawRecalls[i].recallStatus) {
				case 5:
					rawRecalls[i].recallStatus = 'Quit';
					break;
				case 2:
					rawRecalls[i].recallStatus = 'Completed';
					break;
				}

				rawRecalls[i].dateReported = rawRecalls[i].dateReported.slice(0, rawRecalls[i].dateReported.indexOf('T'));
			}

			return rawRecalls;
		},
		openViewReportDialog (rowInfo) {
			this.rowReportInfo = rowInfo;
			this.viewReportDialog = true;
		},
		openCreateReportDialog (rowInfo) {
			this.rowReportInfo = rowInfo;
			this.createReportDialog = true;
		},
		async viewReport () {
			// Make the url for the RNR
			var rnrLink = this.makeRnrReport();

			// Open the RNR in a new window
			window.open(rnrLink);

			// Close the dialog
			this.viewReportDialog = false;
			this.createReportDialog = false;

			// Flip our watcher flag to reload the data after profile data is created
			this.isProfileDataCreated = true;
		},
		cancelCreateReport () {
			this.createReportDialog = false;
			this.rowReportInfo.age = null;
			this.rowReportInfo.gender = null;
			this.rowReportInfo.breastFeed = false;
			this.rowReportInfo.pregnant = false;
		},
		makeRnrReport () {
			const rnrUrl = `${utilityService.getRespondentUiUrl()}?researcher=true` +
				`&recallId=${this.rowReportInfo.recallId}` +
				`&studyVersion=${this.selectedStudy.version}` +
				`&language=${this.rowReportInfo.language}` +
				`&age=${this.rowReportInfo.age}` +
				`&breastFeed=${this.rowReportInfo.breastFeed}` +
				`&pregnant=${this.rowReportInfo.pregnant}` +
				'&energyProfile=MODERATE' +
				'&source=RESEARCHER' +
				`&gender=${this.rowReportInfo.gender}` +
				`&token=${authTokenService.getToken()}`;

			return rnrUrl;
		},
		getFormattedDate (date) {
			if (date && date.length) {
				console.log(date);
				return utilityService.formatDate(date)
			}

			return 'N/A';
		},
		getFormattedRespondentRequest (source, isRefused) {
			if (source === 'RESPONDENT' && !isRefused) {
				return 'Yes';
			} else if (source === 'RESPONDENT' && isRefused) {
				return 'Refused';
			}
			return 'No';
		},
		checkExpandedTwo: function () {
			this.isExpanded2 = this.$refs.autocomplete2.isMenuActive.toString()
		},
		checkExpandedThree: function () {
			this.isExpanded3 = this.$refs.autocomplete3.isMenuActive.toString()
		}
	}
}
</script>

<style>
</style>
